/**
 * DraftPanel - World-Class Legal Document Editor
 * 
 * Modern law firm aesthetic - clean, professional, authoritative
 * Left-aligned, excellent typography, proper spacing
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, Loader2, Copy, Download, Maximize2, Minimize2, FileText, Check, Printer } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { auth } from '../../utils/auth';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import showdown from 'showdown';
import TurndownService from 'turndown';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mdConverter = new showdown.Converter({
  simpleLineBreaks: true,
  strikethrough: true,
  tables: true,
  tasklists: true,
  openLinksInNewWindow: true,
  headerLevelStart: 1
});

const turndownService = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced'
});

const markdownToHtml = (md: string): string => md ? mdConverter.makeHtml(md) : '';
const htmlToMarkdown = (html: string) => html ? turndownService.turndown(html) : '';

interface DraftPanelProps {
  draftId?: string | null;
  onClose: () => void;
  streamingContent?: string;
  isStreaming?: boolean;
}

const DraftPanel: React.FC<DraftPanelProps> = ({ 
  draftId, 
  onClose,
  streamingContent = '',
  isStreaming = false
}) => {
  const { t } = useTranslation(['workspace', 'common']);
  const [draft, setDraft] = useState<any>(null);
  const [versions, setVersions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [markdownContent, setMarkdownContent] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [title, setTitle] = useState('');
  const [currentVersionIndex, setCurrentVersionIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const quillRef = useRef<any>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['clean']
    ],
    clipboard: { matchVisual: false }
  }), []);

  const formats = ['header', 'bold', 'italic', 'underline', 'list', 'indent', 'align'];

  useEffect(() => {
    if (draftId) {
      fetchDraft();
    } else if (isStreaming) {
      setLoading(false);
      setTitle('New Document');
      setMarkdownContent('');
      setHtmlContent('');
      setVersions([]);
      setCurrentVersionIndex(1);
    }
  }, [draftId]);

  useEffect(() => {
    if (isStreaming && streamingContent) {
      setMarkdownContent(streamingContent);
      setHtmlContent(markdownToHtml(streamingContent));
      setLoading(false);
    }
  }, [streamingContent, isStreaming]);

  useEffect(() => {
    if (hasUnsavedChanges && !isStreaming && draftId) {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => autoSave(), 1500);
    }
    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, [markdownContent, title, hasUnsavedChanges, isStreaming, draftId]);

  const fetchDraft = async () => {
    setLoading(true);
    try {
      const token = auth.getToken();
      const response = await axios.get(`${API}/chat/drafts/${draftId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDraft(response.data.draft);
      setVersions(response.data.versions || []);
      const loadedContent = response.data.draft.working_content || '';
      setMarkdownContent(loadedContent);
      setHtmlContent(markdownToHtml(loadedContent));
      setTitle(response.data.draft.title || 'Document');
      setCurrentVersionIndex(response.data.versions?.length || 1);
    } catch (error) {
      if (streamingContent) {
        setMarkdownContent(streamingContent);
        setHtmlContent(markdownToHtml(streamingContent));
        setTitle('Document');
      }
    } finally {
      setLoading(false);
    }
  };

  const autoSave = async () => {
    if (!draftId || isStreaming) return;
    setSaving(true);
    try {
      const token = auth.getToken();
      await axios.patch(
        `${API}/chat/drafts/${draftId}?title=${encodeURIComponent(title)}`,
        { working_content: markdownContent },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleContentChange = useCallback((html: string) => {
    if (!isStreaming) {
      setHtmlContent(html);
      setMarkdownContent(htmlToMarkdown(html));
      setHasUnsavedChanges(true);
    }
  }, [isStreaming]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    setHasUnsavedChanges(true);
  };

  const navigateVersion = async (direction: number) => {
    const newIndex = currentVersionIndex + direction;
    if (newIndex < 1 || newIndex > versions.length) return;
    const targetVersion = versions[newIndex - 1];
    if (targetVersion) {
      setMarkdownContent(targetVersion.content);
      setHtmlContent(markdownToHtml(targetVersion.content));
      setCurrentVersionIndex(newIndex);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(markdownContent);
      setCopied(true);
      toast.success('Copied');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Could not copy'); }
  };

  const handlePrint = () => window.print();

  const handleExportToWord = async () => {
    try {
      const htmlForWord = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
<style>
@page { size: A4; margin: 2.5cm; }
body { font-family: 'Georgia', 'Times New Roman', serif; font-size: 11pt; line-height: 1.5; color: #1a1a1a; }
h1 { font-size: 16pt; font-weight: 600; margin: 0 0 20pt; letter-spacing: 0.5pt; }
h2 { font-size: 12pt; font-weight: 600; margin: 20pt 0 10pt; }
p { margin: 0 0 10pt; }
ul, ol { margin: 10pt 0 10pt 20pt; }
li { margin-bottom: 4pt; }
table { width: 100%; border-collapse: collapse; margin: 12pt 0; }
th, td { padding: 8pt 12pt; text-align: left; border-bottom: 1px solid #e0e0e0; font-size: 10pt; }
th { font-weight: 600; background: #f8f8f8; }
hr { border: none; border-top: 1px solid #e0e0e0; margin: 16pt 0; }
</style></head><body>${htmlContent}</body></html>`;
      
      const blob = new Blob([htmlForWord], { type: 'application/msword' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title || 'document'}.doc`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Exported');
    } catch { toast.error('Export failed'); }
  };

  if (loading && !streamingContent && !isStreaming) {
    return (
      <div className={cn(
        "border-l border-gray-100 bg-white flex items-center justify-center",
        isFullscreen ? "fixed inset-0 z-50" : "flex-1 min-w-[400px] max-w-[50%]"
      )}>
        <Loader2 className="w-5 h-5 text-gray-300 animate-spin" />
      </div>
    );
  }

  return (
    <div className={cn(
      "bg-white flex flex-col h-full",
      isFullscreen ? "fixed inset-0 z-50" : "flex-1 min-w-[400px] max-w-[50%] border-l border-gray-100"
    )}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-9 h-9 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
            <FileText className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <input
              type="text"
              value={title}
              onChange={handleTitleChange}
              className="w-full text-sm font-semibold text-gray-900 bg-transparent border-0 focus:outline-none p-0 truncate"
              placeholder="Document title..."
            />
            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400">
              {saving && <><Loader2 className="w-3 h-3 animate-spin" /> Saving...</>}
              {hasUnsavedChanges && !saving && "● Unsaved"}
              {!hasUnsavedChanges && !saving && !isStreaming && draftId && "Saved"}
              {isStreaming && <><span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse" /> Generating...</>}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-0.5 ml-3">
          <button onClick={handleCopy} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
          <button onClick={handlePrint} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all">
            <Printer className="w-4 h-4" />
          </button>
          <button onClick={handleExportToWord} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all">
            <Download className="w-4 h-4" />
          </button>
          <button onClick={() => setIsFullscreen(!isFullscreen)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all">
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <div className="w-px h-4 bg-gray-200 mx-1" />
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Document Content */}
      <div className="flex-1 overflow-y-auto bg-white">
        {isStreaming ? (
          <div className="legal-doc">
            <style>{`
              .legal-doc {
                padding: 40px 48px;
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                max-width: 100%;
                color: #1a1a1a;
              }
              
              /* Title - Modern, left-aligned */
              .legal-doc h1 {
                font-size: 20px;
                font-weight: 700;
                margin: 0 0 24px 0;
                letter-spacing: -0.01em;
                color: #0a0a0a;
              }
              
              /* Section headings */
              .legal-doc h2 {
                font-size: 14px;
                font-weight: 700;
                margin: 28px 0 12px 0;
                color: #0a0a0a;
              }
              
              /* Subsection */
              .legal-doc h3 {
                font-size: 13px;
                font-weight: 600;
                margin: 20px 0 8px 0;
                color: #333;
              }
              
              /* Paragraphs */
              .legal-doc p {
                font-size: 14px;
                line-height: 1.7;
                margin: 0 0 12px 0;
                color: #333;
              }
              
              /* Strong/Bold */
              .legal-doc strong {
                font-weight: 600;
                color: #0a0a0a;
              }
              
              /* Horizontal rules - Section dividers */
              .legal-doc hr {
                border: none;
                border-top: 1px solid #e5e5e5;
                margin: 24px 0;
              }
              
              /* Unordered lists */
              .legal-doc ul {
                margin: 12px 0;
                padding: 0 0 0 20px;
                list-style: none;
              }
              
              .legal-doc ul li {
                font-size: 14px;
                line-height: 1.6;
                margin-bottom: 6px;
                color: #333;
                position: relative;
                padding-left: 16px;
              }
              
              .legal-doc ul li::before {
                content: "";
                position: absolute;
                left: 0;
                top: 9px;
                width: 4px;
                height: 4px;
                background: #666;
                border-radius: 50%;
              }
              
              /* Ordered lists */
              .legal-doc ol {
                margin: 12px 0;
                padding: 0 0 0 20px;
                list-style: none;
                counter-reset: item;
              }
              
              .legal-doc ol li {
                font-size: 14px;
                line-height: 1.6;
                margin-bottom: 6px;
                color: #333;
                position: relative;
                padding-left: 24px;
                counter-increment: item;
              }
              
              .legal-doc ol li::before {
                content: counter(item) ".";
                position: absolute;
                left: 0;
                font-weight: 600;
                color: #0a0a0a;
              }
              
              /* Tables - Clean, modern */
              .legal-doc table {
                width: 100%;
                margin: 16px 0;
                border-collapse: collapse;
                font-size: 13px;
              }
              
              .legal-doc th,
              .legal-doc td {
                padding: 10px 14px;
                text-align: left;
                border-bottom: 1px solid #eee;
              }
              
              .legal-doc th {
                font-weight: 600;
                color: #666;
                font-size: 11px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                background: #fafafa;
              }
              
              .legal-doc td {
                color: #333;
              }
              
              /* Blockquotes */
              .legal-doc blockquote {
                margin: 16px 0;
                padding: 12px 16px;
                border-left: 3px solid #0a0a0a;
                background: #fafafa;
                font-style: italic;
                color: #555;
              }
              
              /* Code/Emphasis */
              .legal-doc code {
                background: #f5f5f5;
                padding: 2px 6px;
                border-radius: 3px;
                font-size: 13px;
              }
              
              /* Links */
              .legal-doc a {
                color: #0a0a0a;
                text-decoration: underline;
              }
              
              /* Signature area styling */
              .legal-doc p:last-of-type {
                margin-top: 24px;
              }
            `}</style>
            <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
            <span className="inline-block w-0.5 h-4 bg-gray-900 animate-pulse ml-0.5" />
          </div>
        ) : (
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={htmlContent}
            onChange={handleContentChange}
            modules={modules}
            formats={formats}
            placeholder="Start writing..."
            className="flex-1 flex flex-col draft-quill-editor min-h-0"
          />
        )}
      </div>

      {/* Streaming indicator */}
      {isStreaming && (
        <div className="px-5 py-2.5 border-t border-gray-100 bg-white">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>Generating document...</span>
          </div>
        </div>
      )}

      {/* Version control */}
      {!isStreaming && versions.length > 0 && (
        <div className="flex items-center justify-center gap-3 px-5 py-2.5 border-t border-gray-100">
          <button onClick={() => navigateVersion(-1)} disabled={currentVersionIndex <= 1} 
            className="p-1 text-gray-300 hover:text-gray-500 rounded disabled:opacity-30">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-gray-400">v{currentVersionIndex}/{Math.max(versions.length, 1)}</span>
          <button onClick={() => navigateVersion(1)} disabled={currentVersionIndex >= versions.length}
            className="p-1 text-gray-300 hover:text-gray-500 rounded disabled:opacity-30">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default DraftPanel;
